// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useState, useEffect, useRef } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { experimentalStyled } from "@mui/material";
import MainNavbar from "./MainNavbar";
import SideBar from "./Sidebar";
import NotFound from "../pages/NotFound";
import { APP_CONFIG, PRIVILEGES } from "../Config.js";
import { isAccessGranted } from "../utils/oauth";
import ErrorDialog from "./dialog/ErrorDialog";
import AllTravelRequests from "./travel_requests/AllTravelRequests";
import NewTravelRequests from "./travel_requests/NewTravelRequests";
import AllTravelDocumentList from "./travel_document/AllTravelDocumentList";
import AllVisaList from "./visa/AllVisaList";
import ManageTravelRequest from "./travel_requests/ManageTravelRequest";
import UserProfile from "./profile/UserProfile";
import Settings from "./settings/Settings";
import AllInvoices from "./invoice/AllInvoices";
import AllPerDiemPayments from "./cost/AllPerDiemPayments.js";
import MyTravelRequests from "./travel_requests/MyTravelRequests.js";
import Reports from "./report/Reports.js";

const routes = [
  {
    path: APP_CONFIG.PAGES.NEW_TRAVEL_REQUESTS,
    component: NewTravelRequests,
    privileges: [PRIVILEGES.NEW_TRAVEL_REQUESTS],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.ALL_TRAVEL_REQUESTS,
    component: AllTravelRequests,
    privileges: [
      PRIVILEGES.ALL_TRAVEL_REQUESTS,
      PRIVILEGES.AGENT_TRAVEL_REQUESTS,
    ],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.ALL_INVOICES,
    component: AllInvoices,
    privileges: [PRIVILEGES.VIEW_ALL_INVOICES_PAGE],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.ALL_PER_DIEM_PAYMENTS,
    component: AllPerDiemPayments,
    privileges: [PRIVILEGES.VIEW_ALL_PER_DIEM_PAYMENTS_PAGE],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.MY_TRAVEL_REQUESTS,
    component: MyTravelRequests,
    privileges: [PRIVILEGES.MY_TRAVEL_REQUESTS],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.PROFILE,
    component: UserProfile,
    privileges: [PRIVILEGES.VIEW_PROFILE],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.EDIT_TRAVEL_REQUEST + "/:jobNumber",
    component: ManageTravelRequest,
    privileges: [
      PRIVILEGES.EDIT_TRAVEL_REQUEST,
      PRIVILEGES.VIEW_OWN_TRAVEL_REQUEST,
    ],
    exact: false,
  },
  {
    path: APP_CONFIG.PAGES.VIEW_VISA_LIST,
    component: AllVisaList,
    privileges: [PRIVILEGES.VIEW_VISA_LIST],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.VIEW_TRAVEL_DOCUMENT_LIST,
    component: AllTravelDocumentList,
    privileges: [PRIVILEGES.VIEW_TRAVEL_DOCUMENT_LIST],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.SETTINGS,
    component: Settings,
    privileges: [PRIVILEGES.APP_PREFERENCES],
    exact: true,
  },
  {
    path: APP_CONFIG.PAGES.REPORTS,
    component: Reports,
    privileges: [PRIVILEGES.VIEW_ALL_INVOICES_PAGE, PRIVILEGES.VIEW_ALL_PER_DIEM_PAYMENTS_PAGE],
    exact: true,
  },
];

const MainLayoutRoot = experimentalStyled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  height: "100%",
  overflow: "hidden",
  width: "100%",
}));

const MainLayoutWrapper = experimentalStyled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  overflow: "hidden",
  paddingTop: 64,
}));

const MainLayoutContainer = experimentalStyled("div")({
  display: "flex",
  flex: "1 1 auto",
  overflow: "hidden",
});

const MainLayoutContent = experimentalStyled("div")({
  flex: "1 1 auto",
  height: "100%",
  overflow: "auto",
});

const MainLayout = (props) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [hoverActive, setHoverActive] = useState(true);
  const [errorDialog, setErrorDialog] = useState(null);
  const sidebarTimer = useRef(true);

  const handleErrorDialog = (errorContent) => {
    setErrorDialog(errorContent ? errorContent : null);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
    setHoverActive(false);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setHoverActive(false);
    setTimeout(() => {
      setHoverActive(true);
    }, 250);
  };

  const handleDrawerHoverOver = () => {
    sidebarTimer.current = true;
    if (hoverActive && !open) {
      setTimeout(() => {
        if (sidebarTimer.current) {
          setOpen(true);
        }
      }, 100);
    }
  };

  const handleDrawerHoverOut = () => {
    sidebarTimer.current = false;
    if (hoverActive && open) {
      setOpen(false);
    }
  };

  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  const getFirstAccessibleRoute = () => {
    for (let route of routes) {
      if (isAccessGranted(route.privileges)) {
        return route.path;
      }
    }
    return null;
  };

  const firstAccessibleRoute = getFirstAccessibleRoute();

  return (
    <React.Fragment>
      <MainLayoutRoot>
        <MainNavbar
          open={open}
          hoverActive={hoverActive}
          handleDrawerOpen={handleDrawerOpen}
          handleDrawerClose={handleDrawerClose}
          handleDrawerHoverOver={handleDrawerHoverOver}
          handleDrawerHoverOut={handleDrawerHoverOut}
        />
        <div onMouseOver={handleDrawerHoverOver}>
          <SideBar open={open} hoverActive={hoverActive} page={page} />
        </div>
        <MainLayoutWrapper onMouseOver={handleDrawerHoverOut}>
          <MainLayoutContainer>
            <MainLayoutContent>
              <Switch>
                {firstAccessibleRoute && (
                  <Redirect exact from="/" to={firstAccessibleRoute} />
                )}
                {routes.map(
                  (route, index) =>
                    isAccessGranted(route.privileges) && (
                      <Route
                        key={index}
                        exact={route.exact}
                        path={route.path}
                        component={route.component}
                      />
                    )
                )}
                <Route path={APP_CONFIG.PAGES.NOT_FOUND} component={NotFound} />
                <Route path="*" component={NotFound} />
              </Switch>
            </MainLayoutContent>

            {errorDialog && (
              <ErrorDialog
                open={Boolean(errorDialog)}
                message={errorDialog?.message}
                handleOpen={handleErrorDialog}
              />
            )}
          </MainLayoutContainer>
        </MainLayoutWrapper>
      </MainLayoutRoot>
    </React.Fragment>
  );
};

export default MainLayout;